import { useEffect, useState, useCallback, useMemo } from "react";
import { Search, RefreshCw, Mail, FileText, Receipt, Truck, X, AlertTriangle, Eye, Calendar, Check, Inbox, Trash2, User, Repeat, Download, BarChart3, Send, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { adminNudgePendingOrder,
  adminListOrders, adminGetOrder, adminUpdateOrder, adminResendOrderConfirmation, adminForceConfirmOrder,
  adminDeleteOrder, adminSetOrderNotes, adminCustomerProfile, adminRefundOrder,
  adminMarkShipped, adminSetFulfillment, adminOrderPrinterPdfUrl, adminOrderInvoicePdfUrl,
  adminEmailInvoice, adminCreateReorderInvoice, adminScanMockups, adminRepairMockups,
} from "../../lib/api";
import { replyInboxOrder } from "../../lib/replyInbox";
import { UrgencyBadge, urgencyLevel, ageLabel, hoursSince, ExportCsvButton, downloadCsv } from "./shared";
import OrderFilesGallery from "./OrderFilesGallery";

// Stage progression — used by the dropdown + bulk actions. `delivered` is
// kept as an accepted alias by the backend (and migrated to `completed` at
// startup) so existing data doesn't break, but the UI presents the modern
// 6-stage flow.
export const STATUSES = [
  "pending", "paid",
  // DTF-specific intermediate states surface in the dropdown so orders
  // containing DTF transfers can be walked through file review.
  "artwork_received", "file_check_required", "approved_for_print",
  "in_production", "printed", "ready_for_pickup", "shipped", "completed",
];
const STATUS_LABEL = {
  pending: "Pending", paid: "Paid",
  artwork_received: "Artwork Received",
  file_check_required: "File Check Required",
  approved_for_print: "Approved for Print",
  in_production: "In Production",
  printed: "Printed", ready_for_pickup: "Ready for Pickup",
  shipped: "Shipped", completed: "Completed",
  delivered: "Completed",
};
// Statuses that trigger a customer-facing stage email on the backend
// (mirrors services/email.py STAGE_EMAIL_RENDERERS). Used only to show the
// admin a heads-up that saving will email the customer.
const EMAIL_STAGES = new Set(["in_production", "printed", "ready_for_pickup", "completed"]);

export function OrdersTab({ pw, onJump }) {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showSalesReport, setShowSalesReport] = useState(false);
  // Bulk selection — Set of order ids currently checked
  const [bulkIds, setBulkIds] = useState(() => new Set());
  const [bulkStatus, setBulkStatus] = useState(STATUSES[1] || "in_production");
  const [bulkBusy, setBulkBusy] = useState(false);
  // List controls: search / status filter / sort / pagination
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortKey, setSortKey] = useState("smart");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const reload = useCallback(() => {
    adminListOrders(pw)
      .then((d) => setOrders(Array.isArray(d) ? d : []))
      .catch((e) => toast.error(`Failed to load orders: ${e?.response?.data?.detail || e.message}`));
  }, [pw]);
  useEffect(reload, [reload]);
  const [mockupBusy, setMockupBusy] = useState(false);
  const fixMockups = useCallback(async () => {
    if (mockupBusy) return;
    setMockupBusy(true);
    const t = toast.loading("Scanning all orders for corrupt mockups…");
    try {
      const scan = await adminScanMockups(pw);
      if (!scan.corrupt_count) {
        toast.success(`All clear — scanned ${scan.scanned_orders} orders, no corrupt mockups found.`, { id: t });
        return;
      }
      const list = scan.corrupt.map((c) => `${c.order_number} (${c.side})`).join(", ");
      toast.dismiss(t);
      if (!window.confirm(`Found ${scan.corrupt_count} corrupt mockup(s):\n${list}\n\nRegenerate them server-side now? (Pricing, order data and uploaded artwork are never touched.)`)) {
        return;
      }
      const t2 = toast.loading(`Repairing ${scan.corrupt_count} mockup(s)…`);
      const res = await adminRepairMockups(pw);
      toast.success(`Repaired ${res.repaired} mockup(s)${res.failed ? `, ${res.failed} could not be regenerated` : ""}.`, { id: t2 });
      reload();
    } catch (e) {
      toast.error(`Mockup scan/repair failed: ${e?.response?.data?.detail || e.message}`, { id: t });
    } finally {
      setMockupBusy(false);
    }
  }, [pw, mockupBusy, reload]);

  const update = async (id, status) => {
    try {
      const updated = await adminUpdateOrder(id, { fulfillment_status: status }, pw);
      setOrders((o) => o.map((x) => x.id === id ? updated : x));
      if (selected?.id === id) setSelected(updated);
      toast.success("Order updated");
    } catch { toast.error("Update failed"); }
  };

  const toggleOne = (id) => setBulkIds((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAll = (ids) => setBulkIds((s) => {
    if (ids.every((id) => s.has(id))) return new Set();
    return new Set(ids);
  });
  const clearBulk = () => setBulkIds(new Set());

  const bulkApplyStatus = async () => {
    const ids = Array.from(bulkIds);
    if (!ids.length) return;
    if (!window.confirm(`Mark ${ids.length} order(s) as "${bulkStatus.replace("_", " ")}"?`)) return;
    setBulkBusy(true);
    const results = await Promise.all(ids.map((id) =>
      adminUpdateOrder(id, { fulfillment_status: bulkStatus }, pw)
        .then((u) => ({ id, updated: u }))
        .catch((e) => ({ id, error: e?.response?.data?.detail || "update failed" }))
    ));
    // Count from the resolved results directly — NOT inside a setState
    // updater (that ran async with a stale `ok`/`fail`, which is why the
    // old code showed no confirmation even when the server did apply the
    // change and wrote the audit entry).
    const ok = results.filter((r) => r.updated).length;
    const fail = results.filter((r) => r.error).length;
    setOrders((o) => o.map((x) => {
      const r = results.find((rr) => rr.id === x.id && rr.updated);
      return r ? r.updated : x;
    }));
    setBulkBusy(false);
    clearBulk();
    if (ok) toast.success(`Updated ${ok} order(s) → ${STATUS_LABEL[bulkStatus] || bulkStatus}`);
    if (fail) toast.error(`${fail} order(s) failed to update`);
    if (!ok && !fail) toast.message("No orders updated");
    // Re-pull from the server so the list reflects the authoritative
    // fulfillment_status + timestamps for every affected row.
    reload();
  };

  const bulkDelete = async () => {
    const ids = Array.from(bulkIds);
    if (!ids.length) return;
    // Only allow deletion of unpaid orders — paid orders are revenue records
    const target = orders.filter((o) => ids.includes(o.id) && o.payment_status !== "paid");
    if (target.length === 0) {
      toast.error("All selected orders are PAID — refusing to delete revenue records.");
      return;
    }
    const skipped = ids.length - target.length;
    const msg = skipped > 0
      ? `Delete ${target.length} unpaid order(s)? (${skipped} paid order(s) will be skipped.) This cannot be undone.`
      : `Permanently delete ${target.length} order(s)? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setBulkBusy(true);
    let ok = 0, fail = 0;
    const results = await Promise.all(target.map((o) =>
      adminDeleteOrder(o.id, pw).then(() => { ok += 1; return o.id; }).catch(() => { fail += 1; return null; })
    ));
    const deletedIds = new Set(results.filter(Boolean));
    setOrders((o) => o.filter((x) => !deletedIds.has(x.id)));
    setBulkBusy(false);
    clearBulk();
    if (ok) toast.success(`Deleted ${ok} order(s)`);
    if (fail) toast.error(`${fail} failed`);
    if (skipped > 0) toast.message(`Skipped ${skipped} paid order(s)`);
  };

  // Filter (search + status) → sort → paginate. `sorted` is the FULL
  // filtered set (used by select-all + CSV); `paged` is the visible page.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders.filter((o) => {
      const isOpen = o.payment_status === "paid" && (o.fulfillment_status === "pending" || !o.fulfillment_status);
      if (statusFilter === "paid" && o.payment_status !== "paid") return false;
      if (statusFilter === "pending" && o.payment_status === "paid") return false;
      if (statusFilter === "open" && !isOpen) return false;
      if (statusFilter === "fulfilled" && (o.payment_status !== "paid" || isOpen)) return false;
      if (!q) return true;
      return [o.order_number, o.customer?.name, o.customer?.email, o.customer?.phone]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [orders, query, statusFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    if (sortKey === "newest") return arr.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    if (sortKey === "oldest") return arr.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    if (sortKey === "total_desc") return arr.sort((a, b) => (b.total || 0) - (a.total || 0));
    if (sortKey === "total_asc") return arr.sort((a, b) => (a.total || 0) - (b.total || 0));
    // smart: urgent/stale first, then newest
    const score = (o) => {
      const isOpen = o.payment_status === "paid" && (o.fulfillment_status === "pending" || !o.fulfillment_status);
      const lvl = urgencyLevel(o.created_at, isOpen);
      return lvl === "stale" ? 2 : lvl === "urgent" ? 1 : 0;
    };
    return arr.sort((a, b) => {
      const s = score(b) - score(a);
      if (s !== 0) return s;
      return new Date(b.created_at) - new Date(a.created_at);
    });
  }, [filtered, sortKey]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageClamped = Math.min(page, totalPages);
  const paged = useMemo(
    () => sorted.slice((pageClamped - 1) * pageSize, pageClamped * pageSize),
    [sorted, pageClamped, pageSize],
  );
  useEffect(() => { setPage(1); }, [query, statusFilter, sortKey, pageSize]);

  const exportCsv = () => {
    const headers = [
      "order_number", "created_at", "customer_name", "customer_email", "customer_phone",
      "delivery_method", "address", "city", "province", "postal",
      "item_count", "items_summary", "subtotal", "discount", "auto_discount",
      "tax", "shipping", "total", "currency", "payment_status", "fulfillment_status",
    ];
    const rows = sorted.map((o) => {
      const c = o.customer || {};
      const items = (o.items || []).map((it) => `${it.quantity}x ${it.product_name} [${it.size || ""}]`).join(" | ");
      return {
        order_number: o.order_number,
        created_at: o.created_at,
        customer_name: c.name || "",
        customer_email: c.email || "",
        customer_phone: c.phone || "",
        delivery_method: o.delivery_method || "",
        address: c.address || "",
        city: c.city || "",
        province: c.province || "",
        postal: c.postal || "",
        item_count: (o.items || []).length,
        items_summary: items,
        subtotal: (o.subtotal || 0).toFixed(2),
        discount: (o.discount || 0).toFixed(2),
        auto_discount: (o.auto_discount || 0).toFixed(2),
        tax: (o.tax || 0).toFixed(2),
        shipping: (o.shipping || 0).toFixed(2),
        total: (o.total || 0).toFixed(2),
        currency: (o.currency || "cad").toUpperCase(),
        payment_status: o.payment_status,
        fulfillment_status: o.fulfillment_status,
      };
    });
    const today = new Date().toISOString().slice(0, 10);
    downloadCsv(`tc-orders-${today}.csv`, headers, rows);
    toast.success(`Exported ${rows.length} orders`);
  };

  return (
    <>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-3">
        {bulkIds.size > 0 ? (
          <div className="flex flex-wrap items-center gap-3 bg-[#0d0d0d] border border-[var(--tc-red)] px-4 py-2" data-testid="bulk-action-bar">
            <span className="font-mono text-xs text-zinc-300">{bulkIds.size} selected</span>
            <select
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value)}
              data-testid="bulk-status-select"
              className="bg-black border border-[#222] px-2 py-1 text-xs font-mono"
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
            </select>
            <button
              onClick={bulkApplyStatus}
              disabled={bulkBusy}
              data-testid="bulk-apply-status-btn"
              className="font-mono text-[11px] tracking-widest px-3 py-1.5 border border-zinc-700 hover:bg-[var(--tc-red)] hover:border-[var(--tc-red)] hover:text-white transition-colors disabled:opacity-50"
            >
              {bulkBusy ? "WORKING…" : "APPLY STATUS"}
            </button>
            <button
              onClick={bulkDelete}
              disabled={bulkBusy}
              data-testid="bulk-delete-btn"
              className="font-mono text-[11px] tracking-widest px-3 py-1.5 border border-red-900/60 text-red-400 hover:bg-red-900/20 transition-colors disabled:opacity-50"
            >
              DELETE
            </button>
            <button
              onClick={clearBulk}
              data-testid="bulk-clear-btn"
              className="font-mono text-[11px] tracking-widest text-zinc-500 hover:text-white"
            >
              CLEAR
            </button>
          </div>
        ) : <span />}
        <div className="flex items-center gap-2">
          <button
            data-testid="open-sales-report-btn"
            onClick={() => setShowSalesReport(true)}
            className="font-mono text-[11px] tracking-widest px-3 py-2 border border-[#222] hover:border-[var(--tc-red)] hover:text-white text-zinc-300 flex items-center gap-2 transition-colors"
            title="Download a sales report PDF for a date range"
          >
            <BarChart3 size={13} /> SALES REPORT
          </button>
          <button
            data-testid="fix-mockups-btn"
            onClick={fixMockups}
            disabled={mockupBusy}
            className="font-mono text-[11px] tracking-widest px-3 py-2 border border-[#222] hover:border-[var(--tc-red)] hover:text-white text-zinc-300 flex items-center gap-2 transition-colors disabled:opacity-50"
            title="Scan every order for corrupt (striped) mockups and regenerate them"
          >
            <ImageOff size={13} /> {mockupBusy ? "SCANNING…" : "FIX MOCKUPS"}
          </button>
          <ExportCsvButton onClick={exportCsv} count={sorted.length} testid="export-orders-csv" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="orders-filter-bar">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="orders-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search order #, name, email, phone…"
            className="w-full bg-black border border-[#222] pl-9 pr-3 py-2 text-xs font-mono text-zinc-200 focus:border-[var(--tc-red)] outline-none"
          />
        </div>
        <select
          data-testid="orders-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-black border border-[#222] px-2 py-2 text-xs font-mono text-zinc-200"
        >
          <option value="all">All statuses</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending payment</option>
          <option value="open">Open (paid · unfulfilled)</option>
          <option value="fulfilled">Fulfilled</option>
        </select>
        <select
          data-testid="orders-sort"
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value)}
          className="bg-black border border-[#222] px-2 py-2 text-xs font-mono text-zinc-200"
        >
          <option value="smart">Sort: Smart (urgent first)</option>
          <option value="newest">Sort: Newest</option>
          <option value="oldest">Sort: Oldest</option>
          <option value="total_desc">Sort: Total ↓</option>
          <option value="total_asc">Sort: Total ↑</option>
        </select>
        <span className="font-mono text-[11px] text-zinc-500" data-testid="orders-result-count">{sorted.length} result{sorted.length === 1 ? "" : "s"}</span>
      </div>
      <div className="surface-card overflow-x-auto" data-testid="orders-table">
        <table className="w-full text-left">
          <thead className="bg-[#050505] border-b border-[#222]">
            <tr className="font-mono text-[10px] tracking-widest text-zinc-500">
              <th className="p-4 w-10">
                <input
                  type="checkbox"
                  data-testid="bulk-select-all"
                  checked={sorted.length > 0 && sorted.every((o) => bulkIds.has(o.id))}
                  onChange={() => toggleAll(sorted.map((o) => o.id))}
                  className="accent-[var(--tc-red)] cursor-pointer"
                />
              </th>
              <th className="p-4">ORDER</th><th className="p-4">CUSTOMER</th>
              <th className="p-4">ITEMS</th><th className="p-4">TOTAL</th>
              <th className="p-4">PAYMENT</th><th className="p-4">FULFILLMENT</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-zinc-500">No orders.</td></tr>}
            {paged.map((o) => {
              const isOpen = o.payment_status === "paid" && (o.fulfillment_status === "pending" || !o.fulfillment_status);
              const isChecked = bulkIds.has(o.id);
              return (
              <tr key={o.id} className={`border-b border-[#222] hover:bg-[#0d0d0d] ${isChecked ? "bg-[#141414]" : ""}`} data-testid={`order-row-${o.id}`}>
                <td className="p-4 w-10">
                  <input
                    type="checkbox"
                    data-testid={`bulk-select-${o.id}`}
                    checked={isChecked}
                    onChange={() => toggleOne(o.id)}
                    className="accent-[var(--tc-red)] cursor-pointer"
                  />
                </td>
                <td className="p-4 font-mono text-xs">
                  <div>{o.order_number}</div>
                  <div className="mt-1"><UrgencyBadge createdAt={o.created_at} isOpen={isOpen} /></div>
                  <div className="font-mono text-[10px] text-zinc-600 mt-1">{ageLabel(hoursSince(o.created_at))}</div>
                </td>
                <td className="p-4 text-sm">
                  <div className="font-ui font-bold">{o.customer?.name || "—"}</div>
                  <div className="text-zinc-500 text-xs">{o.customer?.email}</div>
                </td>
                <td className="p-4 text-sm">{o.items?.length || 0}</td>
                <td className="p-4 font-mono text-[var(--tc-red)]">${(o.total || 0).toFixed(2)}</td>
                <td className="p-4">
                  <span className={`chip ${o.payment_status === "paid" ? "border-green-700 text-green-400" : "border-yellow-700 text-yellow-500"}`}>{o.payment_status}</span>
                </td>
                <td className="p-4">
                  <FulfillmentBadge value={o.fulfillment_status || "paid"} />
                </td>
                <td className="p-4">
                  <button data-testid={`view-order-${o.id}`} onClick={() => setSelected(o)} className="font-mono text-xs tracking-widest text-zinc-400 hover:text-white">VIEW →</button>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>
      {sorted.length > pageSize && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-3" data-testid="orders-pagination">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[11px] text-zinc-500">Rows:</span>
            <select
              data-testid="orders-page-size"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="bg-black border border-[#222] px-2 py-1 text-xs font-mono text-zinc-200"
            >
              {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-[11px] text-zinc-500" data-testid="orders-page-info">
              Page {pageClamped} of {totalPages} · {sorted.length} orders
            </span>
            <button
              data-testid="orders-prev-page"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pageClamped <= 1}
              className="font-mono text-[11px] tracking-widest px-3 py-1.5 border border-[#222] hover:border-white disabled:opacity-40 transition-colors"
            >
              ← PREV
            </button>
            <button
              data-testid="orders-next-page"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={pageClamped >= totalPages}
              className="font-mono text-[11px] tracking-widest px-3 py-1.5 border border-[#222] hover:border-white disabled:opacity-40 transition-colors"
            >
              NEXT →
            </button>
          </div>
        </div>
      )}
      {selected && (
        <OrderDetailModal
          order={selected}
          onClose={() => setSelected(null)}
          pw={pw}
          onJump={onJump}
          onUpdated={(updated) => {
            setSelected(updated);
            setOrders((arr) => arr.map((x) => x.id === updated.id ? updated : x));
          }}
        />
      )}
      {showSalesReport && (
        <SalesReportModal pw={pw} onClose={() => setShowSalesReport(false)} />
      )}
    </>
  );
}

export function OrderDetailModal({ order: orderProp, onClose, pw, onUpdated, onJump }) {
  // The list response is trimmed for speed (no design layers / etsy_raw), so
  // pull the full order on open and render from that.
  const [order, setOrder] = useState(orderProp);
  useEffect(() => {
    setOrder(orderProp);
    adminGetOrder(orderProp.id, pw).then((full) => { if (full) setOrder(full); }).catch(() => {});
  }, [orderProp.id, pw]);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(!!order.email_sent);
  const [deleting, setDeleting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [nudging, setNudging] = useState(false);
  const [lastNudgedAt, setLastNudgedAt] = useState(order.last_nudged_at || null);
  const [nudgeCount, setNudgeCount] = useState(order.nudge_count || 0);

  const handleResend = async () => {
    if (!window.confirm(`Re-send order confirmation email to ${order.customer?.email}?`)) return;
    setResending(true);
    try {
      const r = await adminResendOrderConfirmation(order.id, pw);
      toast.success(r.email_sent ? "Confirmation email sent" : "Email service returned no-op");
      setResent(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resend confirmation");
    } finally {
      setResending(false);
    }
  };

  const handleForceConfirm = async () => {
    // iter47q: require a reason so every manual confirmation is auditable.
    // The "lost $66.96" incident (TC-20260618-577E40) happened because
    // someone force-confirmed an order without leaving a paper trail and
    // the team thought Square had actually charged the card.
    const reason = window.prompt(
      `FORCE CONFIRM ORDER ${order.order_number} ($${(order.total || 0).toFixed(2)})\n\n` +
      `This will mark the order PAID and send customer + admin emails — but it will NOT charge Square.\n\n` +
      `Why are you confirming this manually? (required, e.g. "Verified Square charge SQ-xyz" or "E-transfer received from Steven on 2026-06-20")`,
      ""
    );
    if (reason === null) return; // user clicked Cancel
    if (!reason || reason.trim().length < 6) {
      toast.error("Reason must be at least 6 characters");
      return;
    }
    setConfirming(true);
    try {
      const r = await adminForceConfirmOrder(order.id, pw, reason.trim());
      toast.success(r.emails_sent ? `Order marked PAID — emails sent` : `Order marked PAID — email sending failed, check logs`);
      onClose();
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to force-confirm order");
    } finally {
      setConfirming(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Permanently delete order ${order.order_number}? This cannot be undone. Only unpaid/phantom orders can be deleted.`)) return;
    setDeleting(true);
    try {
      const r = await adminDeleteOrder(order.id, pw);
      toast.success(`Deleted ${r.order_number} (+${r.payment_transactions_removed} txn, +${r.abandoned_carts_removed} cart)`);
      onClose();
      // Refresh parent list — the cheapest way is a full reload
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete order");
    } finally {
      setDeleting(false);
    }
  };

  const handleCreateReorder = async () => {
    if (!order.customer?.email) {
      toast.error("Order has no customer email — can't create a reorder invoice");
      return;
    }
    if (!window.confirm(
      `Create a draft reorder invoice for ${order.customer?.email} based on order ${order.order_number}? ` +
      `You'll be taken to the Invoices tab to edit prices/quantities before sending.`
    )) return;
    setReordering(true);
    try {
      const inv = await adminCreateReorderInvoice(order.id, pw);
      toast.success(`Draft invoice ${inv.number} created — opening editor…`);
      sessionStorage.setItem("tc_admin_open_invoice", inv.id);
      onClose();
      if (onJump) onJump("invoices");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create reorder invoice");
    } finally {
      setReordering(false);
    }
  };

  const handleNudge = async () => {
    const email = order.customer?.email;
    if (!email) { toast.error("Order has no customer email to nudge"); return; }
    if (!window.confirm(
      `Send a friendly cart-recovery nudge to ${email}?\n\n` +
      `They'll get a "your order is waiting" email with a 10% discount code (WELCOME10) ` +
      `to complete their payment. This does NOT mark the order as paid.`
    )) return;
    setNudging(true);
    try {
      const r = await adminNudgePendingOrder(order.id, pw);
      const nowIso = new Date().toISOString();
      setLastNudgedAt(nowIso);
      setNudgeCount((n) => (n || 0) + 1);
      if (onUpdated) onUpdated({ ...order, last_nudged_at: nowIso,
                                    nudge_count: (nudgeCount || 0) + 1 });
      toast.success(`Nudge sent to ${r.to} · code ${r.code}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nudge failed");
    } finally {
      setNudging(false);
    }
  };



  const isPaid = order.payment_status === "paid";
  const isRefunded = order.payment_status === "refunded";
  const isPartiallyRefunded = order.payment_status === "partially_refunded";
  const canRefund = isPaid || isPartiallyRefunded;
  const [showRefund, setShowRefund] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showShip, setShowShip] = useState(false);
  const customerEmail = order.customer?.email || "";

  // Print-job badge shown next to the order number in the header.
  const fulfillment = order.fulfillment_status || (isPaid ? "paid" : "pending");

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="surface-card max-w-3xl w-full max-h-[85vh] overflow-y-auto" data-testid="order-detail">
        <div className="p-6 border-b border-[#222] flex justify-between items-start">
          <div>
            <div className="font-mono text-[11px] tracking-widest text-zinc-500">/ ORDER</div>
            <h3 className="font-display text-3xl mt-1">{order.order_number}</h3>
            <div className="font-mono text-xs text-zinc-500 mt-1">{new Date(order.created_at).toLocaleString()}</div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <FulfillmentBadge value={fulfillment} />
              {fulfillment === "shipped" && order.tracking_number && (
                <span className="font-mono text-[10px] tracking-widest text-emerald-300 inline-flex items-center gap-1">
                  <Truck size={10} /> {order.tracking_carrier} · {order.tracking_number}
                </span>
              )}
            </div>
            {isPaid && (
              <StageAdvanceRow
                order={order}
                pw={pw}
                onAdvanced={(newOrder) => {
                  if (onUpdated) onUpdated(newOrder);
                }}
              />
            )}
            <FulfillmentStatusControl
              order={order}
              pw={pw}
              onUpdated={(updated) => { setOrder(updated); if (onUpdated) onUpdated(updated); }}
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {customerEmail && (
              <a
                href={replyInboxOrder(order)}
                target="_blank" rel="noreferrer"
                data-testid={`reply-inbox-${order.id}`}
                className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-[var(--tc-blue)] text-[var(--tc-blue)] hover:bg-[var(--tc-blue)] hover:text-white transition-colors"
                title="Open default mail client with a pre-filled reply"
              >
                <Inbox size={14} /> Reply
              </a>
            )}
            {isPaid && (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  fetch(adminOrderPrinterPdfUrl(order.id), { headers: { "X-Admin-Password": pw } })
                    .then(async (r) => {
                      if (r.ok) return r.blob();
                      let detail = `HTTP ${r.status}`;
                      try { const j = await r.json(); if (j?.detail) detail = j.detail; } catch { /* not JSON */ }
                      throw new Error(detail);
                    })
                    .then((blob) => {
                      const u = URL.createObjectURL(blob);
                      window.open(u, "_blank");
                      setTimeout(() => URL.revokeObjectURL(u), 5000);
                    })
                    .catch((err) => toast.error(`PDF export failed: ${err.message || err}`, { duration: 8000 }));
                }}
                data-testid={`printer-pdf-${order.id}`}
                className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-zinc-700 hover:border-white transition-colors cursor-pointer"
                title="Download a single PDF with all mockups + uploaded artwork for the printer"
              >
                <FileText size={14} /> Printer PDF
              </a>
            )}
            {isPaid && (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  fetch(adminOrderInvoicePdfUrl(order.id), { headers: { "X-Admin-Password": pw } })
                    .then(async (r) => {
                      if (r.ok) return r.blob();
                      let detail = `HTTP ${r.status}`;
                      try { const j = await r.json(); if (j?.detail) detail = j.detail; } catch { /* not JSON */ }
                      throw new Error(detail);
                    })
                    .then((blob) => {
                      const u = URL.createObjectURL(blob);
                      window.open(u, "_blank");
                      setTimeout(() => URL.revokeObjectURL(u), 5000);
                    })
                    .catch((err) => toast.error(`Invoice export failed: ${err.message || err}`, { duration: 8000 }));
                }}
                data-testid={`invoice-pdf-${order.id}`}
                className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-[var(--tc-blue)] text-[var(--tc-blue)] hover:bg-[var(--tc-blue)] hover:text-white transition-colors cursor-pointer"
                title="Download the customer-facing tax-inclusive invoice PDF"
              >
                <Receipt size={14} /> Invoice PDF
              </a>
            )}
            {isPaid && (order.customer?.email) && (
              <button
                onClick={async () => {
                  if (!window.confirm(`Email the invoice PDF to ${order.customer.email}?`)) return;
                  try {
                    const r = await adminEmailInvoice(order.id, {}, pw);
                    toast.success(r.sent ? "Invoice emailed to customer" : "Email service returned no-op");
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Email invoice failed");
                  }
                }}
                data-testid={`email-invoice-${order.id}`}
                className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-[var(--tc-blue)] text-[var(--tc-blue)] hover:bg-[var(--tc-blue)] hover:text-white transition-colors"
                title="Email the invoice PDF to the customer as an attachment"
              >
                <Mail size={14} /> Email Invoice
              </button>
            )}
            {canRefund && !isRefunded && (
              <button
                onClick={() => setShowRefund(true)}
                data-testid={`refund-btn-${order.id}`}
                className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-amber-700 text-amber-400 hover:bg-amber-900/20 transition-colors"
                title="Issue a Stripe refund (full or partial) + send customer email"
              >
                <Receipt size={14} /> {isPartiallyRefunded ? "Refund More" : "Refund"}
              </button>
            )}
            {isPaid && fulfillment !== "shipped" && fulfillment !== "delivered" && fulfillment !== "completed" && (
              <button
                onClick={() => setShowShip(true)}
                data-testid={`mark-shipped-btn-${order.id}`}
                className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-emerald-700 text-emerald-300 hover:bg-emerald-900/20 transition-colors"
                title="Mark this order as shipped and email the customer the tracking notification"
              >
                <Truck size={14} /> Mark Shipped
              </button>
            )}
            {isPaid && (
              <button
                onClick={handleResend}
                disabled={resending}
                data-testid={`resend-confirmation-${order.id}`}
                className="btn-tc-secondary inline-flex items-center gap-2 text-xs disabled:opacity-50"
                title={resent ? "Confirmation was already sent — clicking will re-send" : "Send confirmation email to customer + admin alert"}
              >
                <Mail size={14} />
                {resending ? "Sending…" : resent ? "Re-send Email" : "Send Confirmation"}
              </button>
            )}
            <button
              onClick={handleCreateReorder}
              disabled={reordering}
              data-testid={`create-reorder-invoice-${order.id}`}
              className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-indigo-700 text-indigo-300 hover:bg-indigo-900/20 transition-colors disabled:opacity-50"
              title="Create a draft invoice pre-filled with this order's items — edit and send to invite the customer to reorder"
            >
              <Repeat size={14} />
              {reordering ? "Creating…" : "Create Reorder Invoice"}
            </button>
            {!isPaid && customerEmail && (
              <button
                onClick={handleNudge}
                disabled={nudging}
                data-testid={`nudge-pending-${order.id}`}
                className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-amber-600 text-amber-300 hover:bg-amber-900/20 transition-colors disabled:opacity-50"
                title={lastNudgedAt
                  ? `Last nudged ${new Date(lastNudgedAt).toLocaleString()} · ${nudgeCount} total. Click to nudge again.`
                  : "Send a friendly cart-recovery email with a 10% code to nudge the customer to complete payment"}
              >
                <Send size={14} />
                {nudging ? "Sending…"
                  : lastNudgedAt ? `Nudge Again${nudgeCount > 1 ? ` (${nudgeCount})` : ""}`
                                    : "Nudge Customer"}
              </button>
            )}
            {!isPaid && (
              <button
                onClick={handleForceConfirm}
                disabled={confirming}
                data-testid={`force-confirm-${order.id}`}
                className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-green-700 text-green-400 hover:bg-green-900/20 transition-colors disabled:opacity-50"
                title="Mark order as PAID and send customer + admin emails (use only after verifying payment in Stripe Dashboard)"
              >
                <Check size={14} />
                {confirming ? "Confirming…" : "Force Confirm + Email"}
              </button>
            )}
            {!isPaid && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                data-testid={`delete-order-${order.id}`}
                className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-red-900/60 text-red-400 hover:bg-red-900/20 transition-colors disabled:opacity-50"
                title="Permanently delete this phantom/unpaid order"
              >
                <Trash2 size={14} />
                {deleting ? "Deleting…" : "Delete Order"}
              </button>
            )}
            <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
          </div>
        </div>
        <div className="p-6 grid md:grid-cols-2 gap-6 border-b border-[#222]">
          <div>
            <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-2 flex items-center justify-between">
              <span>CUSTOMER</span>
              {customerEmail && (
                <button
                  onClick={() => setShowProfile(true)}
                  data-testid={`customer-profile-${order.id}`}
                  className="font-mono text-[10px] tracking-widest text-[var(--tc-red)] hover:text-white inline-flex items-center gap-1"
                >
                  <User size={11} /> VIEW PROFILE
                </button>
              )}
            </div>
            <div className="text-sm space-y-1">
              <div>{order.customer?.name}</div>
              <div className="text-zinc-400">{order.customer?.email}</div>
              <div className="text-zinc-400">{order.customer?.phone}</div>
              <div className="text-zinc-400">{order.customer?.address}, {order.customer?.city}, {order.customer?.province} {order.customer?.postal}</div>
              <div className="font-mono text-[10px] tracking-widest text-[var(--tc-red)] mt-2">{order.delivery_method?.toUpperCase()}</div>
            </div>
          </div>
          <div>
            <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-2">SUMMARY</div>
            <div className="text-sm space-y-1">
              <div>Subtotal: <span className="font-mono">${order.subtotal?.toFixed(2)}</span></div>
              {order.discount > 0 && <div className="text-[var(--tc-blue)]">Discount ({order.discount_code}): <span className="font-mono">−${order.discount.toFixed(2)}</span></div>}
              <div>Tax: <span className="font-mono">${order.tax?.toFixed(2)}</span></div>
              <div>Shipping: <span className="font-mono">${order.shipping?.toFixed(2)}</span></div>
              <div className="text-[var(--tc-red)] font-display text-2xl mt-2">${order.total?.toFixed(2)}</div>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="font-mono text-[10px] tracking-widest text-zinc-500">ITEMS · {order.items?.length}</div>
          {order.items?.map((it, i) => (
            <div key={i} className="border border-[#222] p-4">
              <div className="flex justify-between">
                <div>
                  <div className="font-ui font-bold uppercase">{it.product_name}</div>
                  <div className="font-mono text-xs text-zinc-500">
                    {it.type === "ready_made" ? "READY-MADE"
                      : it.type === "dtf" ? "DTF TRANSFER"
                      : it.type === "dtf_surcharge" ? "DTF ADD-ONS"
                      : "CUSTOM"} · SIZE {it.size} · QTY {it.quantity}
                  </div>
                </div>
                <div className="font-mono">${it.line_total?.toFixed(2)}</div>
              </div>
              {it.type === "custom" && it.design && (
                <>
                  <div className="mt-3 flex gap-3 items-center">
                    <span className="w-6 h-6 border border-[#222]" style={{ background: it.design.garment_color }} />
                    <span className="font-mono text-xs text-zinc-200 uppercase" data-testid={`order-item-color-${i}`}>
                      {it.design.garment_color_name || it.selected_color || it.design.garment_color}
                    </span>
                    <span className="font-mono text-xs text-zinc-500">· {(it.design.layers || []).length} LAYER(S)</span>
                  </div>
                  {(it.design.layers || []).map((l, j) => (
                    <div key={j} className="mt-2 text-xs text-zinc-400 font-mono pl-4">
                      → [{l.side?.toUpperCase() || "FRONT"}] {l.type}: {l.type === "text" ? `"${l.content}"` : l.type === "image" ? "uploaded image" : "clipart"}
                    </div>
                  ))}
                </>
              )}
              {it.type === "dtf" && (
                <div className="mt-3 space-y-2 text-xs font-mono text-zinc-400" data-testid={`dtf-line-detail-${i}`}>
                  <div>
                    Dimensions: <span className="text-zinc-200">
                      {it.dtf_width_in}" × {it.dtf_length_in || it.dtf_height_in}"
                    </span>
                    {" · "}{it.dtf_sqin} sq in @ ${it.dtf_price_per_sqin}/sq in
                  </div>
                  <div>Kind: <span className="text-zinc-200">{(it.dtf_kind || "").replace(/_/g, " ").toUpperCase()}</span></div>
                  {it.background_removal && (
                    <div className="text-amber-300">⚠ Background removal requested</div>
                  )}
                  {it.notes && (
                    <div className="border-l-2 border-[var(--tc-red)] pl-2 text-zinc-300 whitespace-pre-wrap">
                      Note: {it.notes}
                    </div>
                  )}
                  {it.artwork_url && (
                    <div className="flex gap-2 flex-wrap pt-1">
                      <a
                        href={it.artwork_url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`dtf-artwork-link-${i}`}
                        className="text-[var(--tc-red)] border border-[var(--tc-red)] px-2 py-1 hover:bg-[var(--tc-red)] hover:text-white tracking-widest text-[10px]"
                      >
                        ↓ DOWNLOAD ARTWORK
                      </a>
                      {it.mockup_png && (
                        <a
                          href={it.mockup_png}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`dtf-mockup-link-${i}`}
                          className="text-[var(--tc-blue)] border border-[var(--tc-blue)] px-2 py-1 hover:bg-[var(--tc-blue)] hover:text-white tracking-widest text-[10px]"
                        >
                          ↓ GANG SHEET PREVIEW
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )}
              {it.type === "dtf_surcharge" && (it.dtf_addons_breakdown || []).length > 0 && (
                <div className="mt-2 space-y-1 text-xs font-mono text-zinc-400">
                  {it.dtf_addons_breakdown.map((b, j) => (
                    <div key={j} className="flex justify-between">
                      <span>{b.label}</span>
                      <span>+${b.amount.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          <OrderFilesGallery orderId={order.id} pw={pw} />
          <InternalNotesEditor
            initial={order.internal_notes || ""}
            onSave={(notes) => adminSetOrderNotes(order.id, notes, pw)}
            testidPrefix="order-notes"
          />
        </div>
      </div>
      {showRefund && <RefundDialog order={order} pw={pw} onClose={() => setShowRefund(false)} onDone={() => window.location.reload()} />}
      {showShip && <MarkShippedDialog order={order} pw={pw} onClose={() => setShowShip(false)} onDone={() => window.location.reload()} />}
      {showProfile && <CustomerProfileModal email={customerEmail} pw={pw} onClose={() => setShowProfile(false)} />}
    </div>
  );
}

export function InternalNotesEditor({ initial, onSave, testidPrefix }) {
  const [val, setVal] = useState(initial || "");
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(val);
      toast.success("Notes saved");
    } catch {
      toast.error("Failed to save notes");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="mt-4 border-t border-[#222] pt-4" data-testid={`${testidPrefix}-section`}>
      <div className="flex justify-between items-center mb-2">
        <div className="font-mono text-[10px] tracking-widest text-zinc-500">INTERNAL NOTES (admin-only)</div>
        <button
          onClick={handleSave}
          disabled={saving || val === initial}
          data-testid={`${testidPrefix}-save`}
          className="font-mono text-[10px] tracking-widest px-3 py-1 border border-zinc-700 hover:bg-[var(--tc-red)] hover:border-[var(--tc-red)] hover:text-white transition-colors disabled:opacity-30"
        >
          {saving ? "SAVING…" : "SAVE NOTES"}
        </button>
      </div>
      <textarea
        data-testid={`${testidPrefix}-input`}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        rows={3}
        placeholder="e.g. customer requested matte finish, embroidery moved to left chest…"
        className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 font-body text-sm outline-none resize-none"
      />
    </div>
  );
}


// ===== FULFILLMENT BADGE =====

export const FULFILLMENT_COLORS = {
  pending:              { bg: "bg-zinc-900",    border: "border-zinc-700",    text: "text-zinc-400",    label: "PENDING" },
  paid:                 { bg: "bg-blue-950",    border: "border-blue-700",    text: "text-blue-300",    label: "TO PRINT" },
  artwork_received:     { bg: "bg-cyan-950",    border: "border-cyan-700",    text: "text-cyan-300",    label: "ARTWORK RECEIVED" },
  file_check_required:  { bg: "bg-amber-950",   border: "border-amber-700",   text: "text-amber-300",   label: "FILE CHECK REQUIRED" },
  approved_for_print:   { bg: "bg-teal-950",    border: "border-teal-700",    text: "text-teal-300",    label: "APPROVED FOR PRINT" },
  in_production:        { bg: "bg-amber-950",   border: "border-amber-700",   text: "text-amber-300",   label: "IN PRODUCTION" },
  printed:              { bg: "bg-orange-950",  border: "border-orange-700",  text: "text-orange-300",  label: "PRINTED" },
  ready_for_pickup:     { bg: "bg-purple-950",  border: "border-purple-700",  text: "text-purple-300",  label: "READY FOR PICKUP" },
  shipped:              { bg: "bg-emerald-950", border: "border-emerald-700", text: "text-emerald-300", label: "SHIPPED" },
  completed:            { bg: "bg-green-950",   border: "border-green-700",   text: "text-green-300",   label: "COMPLETED" },
  delivered:            { bg: "bg-green-950",   border: "border-green-700",   text: "text-green-300",   label: "COMPLETED" },
};

export function FulfillmentBadge({ value }) {
  // Normalise legacy/unknown values so the badge always reads something sane.
  const normalised = FULFILLMENT_COLORS[value] ? value : "paid";
  const c = FULFILLMENT_COLORS[normalised];
  return (
    <span
      data-testid={`fulfillment-badge-${normalised}`}
      className={`inline-block px-2.5 py-1 border ${c.bg} ${c.border} ${c.text} font-mono text-[10px] tracking-widest uppercase`}
    >
      {c.label}
    </span>
  );
}

// ===== STAGE ADVANCE ROW =====
// Renders the "next stage" CTA + an inline "send customer email" checkbox.
// Shown inside the OrderDetailModal so the admin can walk an order through
// `paid → in_production → printed → ready_for_pickup / shipped → completed`
// with one click. `shipped` uses the existing Mark Shipped dialog so the
// tracking number / carrier can be captured — we render a passthrough CTA.

// What comes next after each stage. `delivery_method=pickup` gets the
// pickup-only path (skips `shipped`); `shipping` skips `ready_for_pickup`.
function nextStagesFor(order) {
  const cur = order.fulfillment_status || "paid";
  const isPickup = (order.delivery_method || "").toLowerCase() === "pickup";
  const chain = isPickup
    ? ["paid", "in_production", "printed", "ready_for_pickup", "completed"]
    : ["paid", "in_production", "printed", "shipped", "completed"];
  const i = chain.indexOf(cur);
  if (i < 0) return [];
  return chain.slice(i + 1, i + 2); // just the next stage
}

const STAGE_PRETTY = {
  paid: "Paid",
  in_production: "In Production",
  printed: "Printed",
  ready_for_pickup: "Ready for Pickup",
  shipped: "Shipped",
  completed: "Completed",
};

// ===== FULFILLMENT STATUS CONTROL (order detail) =====
// Direct status setter on the order detail view: a dropdown pre-set to the
// order's current status (same options as the bulk action bar) + a Save
// button. Save PATCHes /admin/orders/{id} which (a) applies the status,
// (b) writes an ADMIN_UPDATE_ORDER audit entry, and (c) fires the matching
// customer stage email (e.g. the "ready for pickup" email) when the status
// actually changes and EMAIL CUSTOMER is ticked.
export function FulfillmentStatusControl({ order, pw, onUpdated }) {
  const current = order.fulfillment_status || (order.payment_status === "paid" ? "paid" : "pending");
  const [status, setStatus] = useState(current);
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setStatus(current); }, [current]);

  const dirty = status !== current;
  const emailsOnSave = dirty && sendEmail && EMAIL_STAGES.has(status);

  const save = async () => {
    setBusy(true);
    try {
      const updated = await adminUpdateOrder(
        order.id, { fulfillment_status: status, send_email: sendEmail }, pw,
      );
      toast.success(
        emailsOnSave
          ? `Status saved → ${STATUS_LABEL[status] || status} · customer email sent`
          : `Status saved → ${STATUS_LABEL[status] || status}`,
      );
      if (onUpdated) onUpdated(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 border border-[#222] bg-[#0d0d0d] p-3" data-testid={`fulfillment-control-${order.id}`}>
      <label className="font-mono text-[10px] tracking-widest text-zinc-500">FULFILLMENT STATUS</label>
      <div className="mt-2 flex items-center gap-3 flex-wrap">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="fulfillment-status-select"
          className="bg-black border border-[#222] px-2 py-1.5 text-xs font-mono"
        >
          {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s.replace("_", " ")}</option>)}
        </select>
        <button
          type="button"
          onClick={save}
          disabled={busy || !dirty}
          data-testid="fulfillment-status-save"
          className="font-mono text-[11px] tracking-widest px-4 py-1.5 border border-[var(--tc-red)] text-[var(--tc-red)] hover:bg-[var(--tc-red)] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy ? "SAVING…" : "SAVE"}
        </button>
        <label className="inline-flex items-center gap-2 text-[11px] font-mono tracking-widest text-zinc-400 cursor-pointer select-none">
          <input
            type="checkbox"
            data-testid="fulfillment-send-email"
            checked={sendEmail}
            onChange={(e) => setSendEmail(e.target.checked)}
            className="accent-[var(--tc-red)]"
          />
          EMAIL CUSTOMER
        </label>
      </div>
      {emailsOnSave && (
        <div className="mt-2 font-mono text-[10px] tracking-widest text-emerald-400">
          Will email the customer the “{STATUS_LABEL[status]}” notification on save.
        </div>
      )}
    </div>
  );
}

export function StageAdvanceRow({ order, pw, onAdvanced }) {
  const [busy, setBusy] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const next = nextStagesFor(order)[0];
  if (!next) return null;

  const handleAdvance = async () => {
    // `shipped` needs the tracking dialog — admin should click Mark Shipped
    // instead. We disable the quick-advance for that transition.
    if (next === "shipped") {
      toast.message("Use 'Mark Shipped' to capture tracking + carrier.");
      return;
    }
    setBusy(true);
    try {
      const r = await adminSetFulfillment(order.id, next, pw, sendEmail);
      toast.success(
        sendEmail && r.email_sent
          ? `Advanced → ${STAGE_PRETTY[next]} + email sent`
          : `Advanced → ${STAGE_PRETTY[next]}`,
      );
      if (onAdvanced) onAdvanced({ ...order, fulfillment_status: r.fulfillment_status });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to advance stage");
    } finally {
      setBusy(false);
    }
  };

  if (next === "shipped") {
    return (
      <div className="mt-3 font-mono text-[10px] tracking-widest text-zinc-500">
        Next stage: <span className="text-emerald-300">SHIPPED</span> — use “Mark Shipped” to capture tracking.
      </div>
    );
  }

  return (
    <div className="mt-3 flex items-center gap-3 flex-wrap" data-testid={`stage-advance-row-${order.id}`}>
      <button
        type="button"
        disabled={busy}
        onClick={handleAdvance}
        data-testid={`advance-stage-${order.id}`}
        className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-[var(--tc-red)] text-[var(--tc-red)] hover:bg-[var(--tc-red)] hover:text-white transition-colors disabled:opacity-50"
        title={`Advance order to "${STAGE_PRETTY[next]}"`}
      >
        {busy ? "WORKING…" : `Advance → ${STAGE_PRETTY[next]}`}
      </button>
      <label className="inline-flex items-center gap-2 text-[11px] font-mono tracking-widest text-zinc-400 cursor-pointer select-none">
        <input
          type="checkbox"
          data-testid={`advance-send-email-${order.id}`}
          checked={sendEmail}
          onChange={(e) => setSendEmail(e.target.checked)}
          className="accent-[var(--tc-red)]"
        />
        EMAIL CUSTOMER
      </label>
    </div>
  );
}

// ===== MARK SHIPPED DIALOG =====

export function MarkShippedDialog({ order, onClose, onDone, pw }) {
  const [tracking, setTracking] = useState("");
  const [carrier, setCarrier] = useState("canada_post");
  const [note, setNote] = useState("");
  const [skipEmail, setSkipEmail] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await adminMarkShipped(order.id, { tracking_number: tracking.trim(), carrier, note, skip_email: skipEmail }, pw);
      toast.success(r.email_sent ? "Marked shipped + email sent" : "Marked shipped");
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mark shipped failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="mark-shipped-dialog">
      <div className="surface-card max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[#222] flex justify-between items-center">
          <h3 className="font-display text-xl uppercase">Mark Shipped · {order.order_number}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="font-mono text-[10px] tracking-widest text-zinc-500">CARRIER</label>
            <select value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid="ship-carrier-select" className="w-full bg-black border border-[#222] px-3 py-2 font-mono text-sm mt-1">
              <option value="canada_post">Canada Post (auto-track)</option>
              <option value="ups">UPS (auto-track)</option>
              <option value="usps">USPS (auto-track)</option>
              <option value="fedex">FedEx (auto-track)</option>
              <option value="chit_chats">Chit Chats (auto-track)</option>
              <option value="stallion_express">Stallion Express</option>
              <option value="purolator">Purolator</option>
              <option value="dhl">DHL</option>
              <option value="other">Other</option>
            </select>
            <span className="font-mono text-[10px] text-zinc-500 mt-1 block">
              Auto-track carriers use Shippo for live updates; others get a deep-link to the carrier&rsquo;s tracker.
            </span>
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-widest text-zinc-500">TRACKING NUMBER (optional)</label>
            <input value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid="ship-tracking-input"
              className="w-full bg-black border border-[#222] focus:border-[var(--tc-blue)] px-3 py-2 font-mono text-sm mt-1 outline-none" />
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-widest text-zinc-500">NOTE TO CUSTOMER (optional)</label>
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="ship-note-input"
              placeholder="e.g. Expect 2–3 business days for delivery."
              className="w-full bg-black border border-[#222] focus:border-[var(--tc-blue)] px-3 py-2 font-body text-sm mt-1 outline-none resize-none" />
          </div>
          <label className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-zinc-400 cursor-pointer">
            <input type="checkbox" checked={skipEmail} onChange={(e) => setSkipEmail(e.target.checked)} data-testid="ship-skip-email" className="accent-[var(--tc-red)]" />
            SKIP CUSTOMER EMAIL
          </label>
          <button onClick={submit} disabled={busy} data-testid="ship-submit-btn"
            className="w-full btn-tc-primary disabled:opacity-50 inline-flex items-center justify-center gap-2">
            <Truck size={16} /> {busy ? "Sending…" : "Mark Shipped & Email Customer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== TRAFFIC TAB =====

export function RefundDialog({ order, onClose, onDone, pw }) {
  const total = Number(order.total || 0);
  const alreadyRefunded = Number(order.refund_amount || 0);
  const remaining = Math.max(0, Number((total - alreadyRefunded).toFixed(2)));
  const [amount, setAmount] = useState(remaining.toFixed(2));
  const [reason, setReason] = useState("requested_by_customer");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0 || amt > remaining + 0.01) {
      toast.error(`Enter an amount between $0.01 and $${remaining.toFixed(2)}`);
      return;
    }
    if (!window.confirm(`Refund $${amt.toFixed(2)} via Stripe?`)) return;
    setBusy(true);
    try {
      const r = await adminRefundOrder(order.id, { amount: amt, reason, note }, pw);
      toast.success(`Refunded $${(r.amount || amt).toFixed(2)} · total refunded $${(r.total_refunded || amt).toFixed(2)}`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="refund-dialog">
      <div className="surface-card max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[#222] flex justify-between items-center">
          <h3 className="font-display text-xl uppercase">Refund Order {order.order_number}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3">
          {alreadyRefunded > 0 && (
            <div className="p-2 border border-amber-700/50 bg-amber-900/10 font-mono text-[11px] text-amber-300" data-testid="refund-history-banner">
              Already refunded: ${alreadyRefunded.toFixed(2)} of ${total.toFixed(2)}. Remaining refundable: <strong>${remaining.toFixed(2)}</strong>
            </div>
          )}
          <div>
            <label className="font-mono text-[10px] tracking-widest text-zinc-500">AMOUNT (max ${remaining.toFixed(2)})</label>
            <input
              type="number" step="0.01" min="0.01" max={remaining}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="refund-amount-input"
              className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 font-mono text-sm mt-1"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-widest text-zinc-500">STRIPE REASON</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="refund-reason-select"
              className="w-full bg-black border border-[#222] px-3 py-2 font-mono text-sm mt-1"
            >
              <option value="requested_by_customer">Requested by customer</option>
              <option value="duplicate">Duplicate</option>
              <option value="fraudulent">Fraudulent</option>
            </select>
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-widest text-zinc-500">CUSTOMER-FACING NOTE (optional)</label>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="refund-note-input"
              placeholder="Shown in the refund email to the customer."
              className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 font-body text-sm mt-1 outline-none resize-none"
            />
          </div>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="refund-submit-btn"
            className="w-full btn-tc-primary disabled:opacity-50"
          >
            {busy ? "Processing…" : `Refund $${(parseFloat(amount) || 0).toFixed(2)}`}
          </button>
          <p className="font-mono text-[10px] text-zinc-500 text-center">Stripe refunds typically take 5–10 business days to return to the customer's card.</p>
        </div>
      </div>
    </div>
  );
}

// ===== CUSTOMER PROFILE MODAL =====

export function CustomerProfileModal({ email, onClose, pw }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    setBusy(true);
    adminCustomerProfile(email, pw).then(setData).catch(() => setData(null)).finally(() => setBusy(false));
  }, [email, pw]);
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-end p-0 md:p-6" onClick={onClose} data-testid="customer-profile-modal">
      <div className="surface-card max-w-2xl w-full max-h-[95vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[#222] flex justify-between items-center sticky top-0 bg-[#0a0a0a] z-10">
          <div>
            <div className="font-mono text-[10px] tracking-widest text-zinc-500">/ CUSTOMER</div>
            <h3 className="font-display text-2xl uppercase">{data?.name || email}</h3>
            <a href={`mailto:${email}`} className="font-mono text-xs text-zinc-400 hover:text-[var(--tc-red)]">{email}</a>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X size={20} /></button>
        </div>
        {busy && <div className="p-8 text-center font-mono text-xs text-zinc-500">Loading…</div>}
        {!busy && data && (
          <div className="p-5 space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="surface-card p-3">
                <div className="font-mono text-[10px] tracking-widest text-zinc-500">LIFETIME VALUE</div>
                <div className="font-display text-2xl text-[var(--tc-red)]">${data.lifetime_value.toFixed(2)}</div>
              </div>
              <div className="surface-card p-3">
                <div className="font-mono text-[10px] tracking-widest text-zinc-500">PAID ORDERS</div>
                <div className="font-display text-2xl">{data.paid_order_count}</div>
              </div>
              <div className="surface-card p-3">
                <div className="font-mono text-[10px] tracking-widest text-zinc-500">QUOTES</div>
                <div className="font-display text-2xl">{data.total_quote_count}</div>
              </div>
              <div className="surface-card p-3">
                <div className="font-mono text-[10px] tracking-widest text-zinc-500">SINCE</div>
                <div className="font-mono text-sm mt-1">{data.first_seen_at ? new Date(data.first_seen_at).toLocaleDateString() : "—"}</div>
              </div>
            </div>

            {data.orders.length > 0 && (
              <div>
                <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-2">ORDERS · {data.orders.length}</div>
                <ul className="space-y-1">
                  {data.orders.slice(0, 20).map((o) => (
                    <li key={o.id} className="flex justify-between items-center bg-black border border-[#222] px-3 py-2">
                      <div className="font-mono text-xs">
                        <span className="text-white">{o.order_number}</span>
                        <span className="text-zinc-500"> · {new Date(o.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`font-mono text-[10px] tracking-widest ${o.payment_status === "paid" ? "text-green-400" : "text-zinc-500"}`}>{o.payment_status}</span>
                        <span className="font-mono text-sm">${Number(o.total || 0).toFixed(2)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {data.quotes.length > 0 && (
              <div>
                <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-2">QUOTES · {data.quotes.length}</div>
                <ul className="space-y-1">
                  {data.quotes.slice(0, 20).map((q) => (
                    <li key={q.id} className="flex justify-between items-center bg-black border border-[#222] px-3 py-2">
                      <div className="font-mono text-xs">
                        <span className="text-white">{q.request_number}</span>
                        <span className="text-zinc-500"> · {new Date(q.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] tracking-widest text-zinc-400">{q.product_type}</span>
                        <span className="font-mono text-sm">{q.quantity} pc</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// ===== SALES REPORT MODAL =====
//
// Downloadable unified sales report (Orders + Invoices) as a branded PDF.
// Backend endpoint: GET /api/admin/reports/sales.pdf?start=&end=&range_label=
// Auth uses the X-Admin-Password header, so we can't use a plain <a href>;
// we fetch as blob and trigger a browser download client-side.

function ymd(d) {
  // Local-timezone YYYY-MM-DD (matches what <input type="date"> uses).
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Preset → returns {start, end, label}. `end` is the last full day; the
// server appends T23:59:59 semantics via ISO comparison.
function computePreset(key) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(today);
  const daysAgo = (n) => { const d = new Date(today); d.setDate(d.getDate() - n); return d; };
  switch (key) {
    case "7d":       return { start: ymd(daysAgo(6)),  end: ymd(end), label: "Last 7 days" };
    case "30d":      return { start: ymd(daysAgo(29)), end: ymd(end), label: "Last 30 days" };
    case "90d":      return { start: ymd(daysAgo(89)), end: ymd(end), label: "Last 90 days" };
    case "12mo": {
      const s = new Date(today); s.setFullYear(s.getFullYear() - 1); s.setDate(s.getDate() + 1);
      return { start: ymd(s), end: ymd(end), label: "Last 12 months" };
    }
    case "ytd":      return { start: ymd(new Date(now.getFullYear(), 0, 1)), end: ymd(end), label: "Year to date" };
    case "this_mo":  return {
      start: ymd(new Date(now.getFullYear(), now.getMonth(), 1)),
      end: ymd(end),
      label: "This month",
    };
    case "last_mo": {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const e = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start: ymd(s), end: ymd(e), label: "Last month" };
    }
    case "last_q": {
      const q = Math.floor(now.getMonth() / 3);
      // last quarter = q - 1 (wrapping across year boundary)
      const lastQStartMonth = (q - 1) * 3;
      const yearOffset = lastQStartMonth < 0 ? -1 : 0;
      const startMonth = (lastQStartMonth + 12) % 12;
      const y = now.getFullYear() + yearOffset;
      const s = new Date(y, startMonth, 1);
      const e = new Date(y, startMonth + 3, 0);
      return { start: ymd(s), end: ymd(e), label: "Last quarter" };
    }
    case "all":      return { start: "", end: "", label: "All time" };
    default:         return { start: "", end: "", label: "Custom range" };
  }
}

const PRESETS = [
  { k: "7d",      l: "Last 7d" },
  { k: "30d",     l: "Last 30d" },
  { k: "90d",     l: "Last 90d" },
  { k: "12mo",    l: "Last 12mo" },
  { k: "ytd",     l: "Year to date" },
  { k: "this_mo", l: "This month" },
  { k: "last_mo", l: "Last month" },
  { k: "last_q",  l: "Last quarter" },
  { k: "all",     l: "All time" },
];

function SalesReportModal({ pw, onClose }) {
  const [preset, setPreset] = useState("30d");
  const initial = useMemo(() => computePreset("30d"), []);
  const [start, setStart] = useState(initial.start);
  const [end, setEnd] = useState(initial.end);
  const [label, setLabel] = useState(initial.label);
  const [busy, setBusy] = useState(false);

  const applyPreset = (key) => {
    setPreset(key);
    const p = computePreset(key);
    setStart(p.start);
    setEnd(p.end);
    setLabel(p.label);
  };

  const onDateChange = (which, val) => {
    if (which === "start") setStart(val); else setEnd(val);
    setPreset("custom");
    setLabel("Custom range");
  };

  const download = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams();
      if (start) params.set("start", start);
      if (end)   params.set("end",   `${end}T23:59:59Z`);
      params.set("range_label", label);
      const res = await fetch(
        `${base}/api/admin/reports/sales.pdf?${params.toString()}`,
        { headers: { "X-Admin-Password": pw } },
      );
      if (!res.ok) {
        const err = await res.text().catch(() => "");
        throw new Error(err || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `sales-report-${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded — ${label}`);
    } catch (e) {
      toast.error(`Report failed: ${e.message || e}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 overflow-y-auto"
         onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="surface-card max-w-2xl w-full my-4"
           data-testid="sales-report-modal">
        <div className="p-5 border-b border-[#222] flex justify-between items-center">
          <h3 className="font-display text-2xl uppercase flex items-center gap-3">
            <BarChart3 size={20} className="text-[var(--tc-red)]" /> Sales Report
          </h3>
          <button onClick={onClose} data-testid="sales-report-close"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-5">
          <p className="text-sm text-zinc-400">
            Unified PDF report combining <span className="text-white">paid orders</span> and
            <span className="text-white"> invoices with payments</span> for the selected date range.
            Includes revenue totals, tax collected, discounts, and a full detail table.
          </p>

          {/* Preset chips */}
          <div>
            <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-2">DATE PRESETS</div>
            <div className="flex flex-wrap gap-1.5">
              {PRESETS.map((p) => (
                <button
                  key={p.k}
                  data-testid={`sales-preset-${p.k}`}
                  onClick={() => applyPreset(p.k)}
                  className={`px-3 py-1.5 border font-mono text-[10px] tracking-widest uppercase transition-colors ${
                    preset === p.k
                      ? "bg-[var(--tc-red)] border-[var(--tc-red)] text-white"
                      : "border-[#222] text-zinc-400 hover:text-white hover:border-white"
                  }`}
                >{p.l}</button>
              ))}
            </div>
          </div>

          {/* Custom range */}
          <div>
            <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-2">CUSTOM RANGE</div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="font-mono text-[10px] tracking-widest text-zinc-500">FROM</span>
                <input
                  type="date"
                  value={start}
                  onChange={(e) => onDateChange("start", e.target.value)}
                  data-testid="sales-report-start"
                  className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 mt-1 text-sm outline-none [color-scheme:dark]"
                />
              </label>
              <label className="block">
                <span className="font-mono text-[10px] tracking-widest text-zinc-500">TO</span>
                <input
                  type="date"
                  value={end}
                  onChange={(e) => onDateChange("end", e.target.value)}
                  data-testid="sales-report-end"
                  className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 mt-1 text-sm outline-none [color-scheme:dark]"
                />
              </label>
            </div>
            <div className="mt-2 font-mono text-[10px] text-zinc-500" data-testid="sales-report-range-label">
              Range: <span className="text-amber-300">{label}</span>
              {(start || end) && (
                <> · <span className="text-white">{start || "earliest"}</span> → <span className="text-white">{end || "today"}</span></>
              )}
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-[#222] flex justify-end gap-2">
          <button onClick={onClose} className="btn-tc-secondary" data-testid="sales-report-cancel">
            Cancel
          </button>
          <button
            data-testid="sales-report-download"
            onClick={download}
            disabled={busy}
            className="btn-tc-primary disabled:opacity-50 flex items-center gap-2"
          >
            <Download size={13} />
            {busy ? "Generating…" : "Download PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ===== CUSTOM PRODUCTS CRUD =====

export default OrdersTab;
